/* eslint-disable filename-rules/match -- versioned style-config files are named after the DLS version */
import type { ComponentStyleConfig } from '@chakra-ui/theme';
import type { StyleFunctionProps } from '@chakra-ui/theme-tools';
import { dls } from '@joinrepublic/dls-ui-core';

/**
 * DLS 2.2 Text — the numeric `body-*` scale.
 *
 * Each size **reads** its `textStyle` rather than restating the metrics. The 2.1 config beside
 * it restates them, which is how `textStyles['body-sm-md']` and `Text.sizes['body-sm-md']` came
 * to disagree about a line-height (20px against 22.4px). Spreading the token means
 * `size="body-500"` and `textStyle="body-500"` cannot drift apart.
 *
 * Weight is a prop of the scale, not part of a style name, so `baseStyle` sets regular and a
 * call site asks for `fontWeight="medium"` or `"semibold"`. Figma names 600 "Bold"; 700 is a
 * weight Inter Republic cannot render.
 *
 * `-compact` is a size of its own rather than a modifier — it has its own token, and only two
 * of the six steps have one.
 */
const step = (name: keyof typeof dls.textStyles) => ({
  ...dls.textStyles[name],
});

/**
 * Whether the weight asked for is above regular.
 *
 * Only `body-400` needs this. Figma sets its tracking from its weight — 0% at Regular, −1% at
 * Medium and SemiBold, and the same for its compact twin — and it is the one step in the family
 * that does; every other size keeps one tracking across all three weights. Chakra v2 forwards
 * every component prop into these style functions, which is the same mechanism
 * `versionedStyleConfig` uses to read `data-dls-version`.
 *
 * Resolved through the weight scale and compared, rather than matched against a list of the
 * regular spellings — that list read `'light'` and `300` as emphasis, and tracking follows the
 * weight in both directions.
 *
 * Ceiling: a weight this cannot reduce to a number — a responsive `fontWeight={{ base, md }}`, or
 * one set through a `textStyle` or a class rather than the prop — keeps the regular tracking.
 */
const isEmphasised = (fontWeight: unknown) => {
  const weight =
    typeof fontWeight === 'string'
      ? dls.fontWeights[fontWeight as keyof typeof dls.fontWeights] ??
        Number(fontWeight)
      : fontWeight;

  return typeof weight === 'number' && weight > dls.fontWeights.normal;
};

/** A 14px step, whose tracking tightens once the weight goes above regular. */
const step400 =
  (name: 'body-400' | 'body-400-compact') => (props: StyleFunctionProps) => ({
    ...dls.textStyles[name],
    ...(isEmphasised(props.fontWeight) && { letterSpacing: 'less-tight' }),
  });

export const styleConfig: ComponentStyleConfig = {
  baseStyle: {
    fontWeight: 'normal',
    color: 'neutral.fg-primary',
  },
  sizes: {
    'body-200': step('body-200'),
    'body-300': step('body-300'),
    'body-400': step400('body-400'),
    'body-400-compact': step400('body-400-compact'),
    'body-500': step('body-500'),
    'body-500-compact': step('body-500-compact'),
    'body-600': step('body-600'),
    'body-700': step('body-700'),
  },
  variants: {
    'text-primary': {
      color: 'neutral.fg-primary',
    },
    'text-secondary': {
      color: 'neutral.fg-secondary',
    },
    'text-tertiary': {
      color: 'neutral.fg-tertiary',
    },
    'text-disabled': {
      color: 'neutral.fg-disabled',
    },
  },
  defaultProps: {
    size: 'body-500',
    variant: 'text-primary',
  },
};
